import React, { createContext, useState, useEffect, useContext } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState(() => {
        try {
            const localData = localStorage.getItem('cartItems');
            return localData ? JSON.parse(localData) : [];
        } catch (error) {
            console.error('Error parsing cart data from localStorage:', error);
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('cartItems', JSON.stringify(cartItems));
        } catch (error) {
            console.error('Error saving cart data to localStorage:', error);
        }
    }, [cartItems]);

    const generateCartId = (product, options) => {
        // specific options string to identify unique combinations
        const optionsString = JSON.stringify(options);
        return `${product.productId}-${optionsString}`;
    };

    const addToCart = (product, quantity, options) => {
        const newItemId = generateCartId(product, options);

        setCartItems(prevItems => {
            const existingItemIndex = prevItems.findIndex(item => item.cartId === newItemId);

            if (existingItemIndex > -1) {
                // Item exists, update quantity
                const newItems = [...prevItems];
                const availableStock = product.availableStock || product.stock;
                const newQuantity = newItems[existingItemIndex].quantity + quantity;
                // Cap the quantity at available stock
                newItems[existingItemIndex].quantity = availableStock ? Math.min(newQuantity, availableStock) : newQuantity;
                return newItems;
            } else {
                // New item
                const availableStock = product.availableStock || product.stock;
                const finalQuantity = availableStock ? Math.min(quantity, availableStock) : quantity;
                return [...prevItems, {
                    cartId: newItemId,
                    product,
                    quantity: finalQuantity,
                    options,
                    // Store a localized price or relevant data if needed immediately
                    price: parseFloat(product.price)
                }];
            }
        });
    };

    const removeFromCart = (cartId) => {
        setCartItems(prevItems => prevItems.filter(item => item.cartId !== cartId));
    };

    const updateQuantity = (cartId, newQuantity) => {
        if (newQuantity < 1) return;
        setCartItems(prevItems =>
            prevItems.map(item => {
                if (item.cartId === cartId) {
                    // Get available stock for this item
                    const availableStock = item.product.availableStock || item.product.stock || Infinity;
                    // Don't allow quantity higher than available stock
                    const finalQuantity = Math.min(newQuantity, availableStock);
                    return { ...item, quantity: finalQuantity };
                }
                return item;
            })
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    const cartTotal = cartItems.reduce((total, item) => {
        return total + (item.price * item.quantity);
    }, 0);

    const cartCount = cartItems.reduce((count, item) => {
        return count + item.quantity;
    }, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartTotal,
            cartCount
        }}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;
